import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { esProductoBolsas5kg, redondearABolsasCompletas, calcularNumeroBolsas, KG_POR_BOLSA, ordenarProductosAzucarPrimero } from "@/lib/calculos";
import { Loader2 } from "lucide-react";
import { getDisplayName } from "@/lib/productUtils";

interface PedidoDetalleDialogProps {
  pedidoId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateNext?: () => void;
  onNavigatePrevious?: () => void;
  canNavigateNext?: boolean;
  canNavigatePrevious?: boolean;
}

interface PedidoDetalle {
  id: string;
  folio: string;
  fecha_pedido: string;
  subtotal: number;
  impuestos: number;
  total: number;
  status: string;
  notas: string | null;
  clientes: { nombre: string; codigo: string } | null;
  profiles: { full_name: string } | null;
  cliente_sucursales: { nombre: string } | null;
  pedidos_detalles: Array<{
    id: string;
    cantidad: number;
    precio_unitario: number;
    subtotal: number;
    kilos_totales: number | null;
    unidades_manual: number | null;
    productos: {
      codigo: string;
      nombre: string;
      marca: string | null;
      especificaciones: string | null;
      contenido_empaque: string | null;
      unidad: string;
      precio_por_kilo: boolean;
      peso_kg: number | null;
    };
  }>;
}

export default function PedidoDetalleDialog({
  pedidoId,
  open,
  onOpenChange,
  onNavigateNext,
  onNavigatePrevious,
  canNavigateNext = false,
  canNavigatePrevious = false,
}: PedidoDetalleDialogProps) {
  const [pedido, setPedido] = useState<PedidoDetalle | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (pedidoId && open) {
      loadPedido();
    }
  }, [pedidoId, open]);

  // Navegación con teclado: Flecha derecha = siguiente, Flecha izquierda = anterior
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && canNavigateNext && onNavigateNext) {
        e.preventDefault();
        onNavigateNext();
      } else if (e.key === "ArrowLeft" && canNavigatePrevious && onNavigatePrevious) {
        e.preventDefault();
        onNavigatePrevious();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, canNavigateNext, canNavigatePrevious, onNavigateNext, onNavigatePrevious]);

  const loadPedido = async () => {
    if (!pedidoId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("pedidos")
        .select(`
          id,
          folio,
          fecha_pedido,
          subtotal,
          impuestos,
          total,
          status,
          notas,
          clientes (nombre, codigo),
          profiles:vendedor_id (full_name),
          cliente_sucursales:sucursal_id (nombre),
          pedidos_detalles (
            id,
            cantidad,
            precio_unitario,
            subtotal,
            kilos_totales,
            unidades_manual,
            productos (
              codigo,
              nombre,
              marca,
              especificaciones,
              contenido_empaque,
              unidad,
              precio_por_kilo,
              peso_kg
            )
          )
        `)
        .eq("id", pedidoId)
        .single();

      if (error) throw error;
      setPedido(data as any);
    } catch (error) {
      console.error("Error loading pedido:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const labels: Record<string, string> = {
      pendiente: "Pendiente",
      en_ruta: "En Ruta",
      entregado: "Entregado",
      cancelado: "Cancelado",
    };
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      pendiente: "secondary",
      en_ruta: "default",
      entregado: "default",
      cancelado: "destructive",
    };
    return <Badge variant={variants[status] || "default"}>{labels[status] || status}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Detalle del Pedido {pedido?.folio}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : pedido ? (
          <div className="space-y-6">
            {/* Información general */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Cliente</p>
                <p className="font-medium">{pedido.clientes?.nombre || "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sucursal</p>
                <p className="font-medium">{pedido.cliente_sucursales?.nombre || "Principal"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Vendedor</p>
                <p className="font-medium">{pedido.profiles?.full_name || "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fecha</p>
                <p className="font-medium">{new Date(pedido.fecha_pedido).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estado</p>
                {getStatusBadge(pedido.status)}
              </div>
            </div>

            {/* Notas */}
            {pedido.notas && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Notas</p>
                <p className="text-sm">{pedido.notas}</p>
              </div>
            )}

            {/* Productos */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-center">Presentación</TableHead>
                    <TableHead className="text-right">P. Unitario</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ordenarProductosAzucarPrimero(pedido.pedidos_detalles, (d) => d.productos.nombre).map((detalle) => {
                    const producto = detalle.productos;
                    const nombreLower = producto.nombre.toLowerCase();
                    let unidadComercial = producto.unidad || "pza";
                    
                    // Detectar si es cliente Lecaroz para reglas especiales
                    const esLecaroz = pedido.clientes?.nombre?.toLowerCase().includes('lecaroz');
                    
                    // Regla especial para Lecaroz: Anís y Canela Molida siempre se muestran como "bolsa"
                    if (esLecaroz && (nombreLower.includes('anís') || nombreLower.includes('anis') || nombreLower.includes('canela molida'))) {
                      unidadComercial = 'bolsa';
                    }
                    
                    // Calcular presentación para bodegueros - SIEMPRE en unidades comerciales, nunca solo kg
                    let presentacionDisplay = "";
                    
                    // *** REGLA ESPECIAL ANÍS / CANELA MOLIDA: Bolsas de 5kg ***
                    if (esProductoBolsas5kg(producto.nombre)) {
                      const numBolsas = calcularNumeroBolsas(detalle.cantidad, KG_POR_BOLSA);
                      presentacionDisplay = `${numBolsas} bolsa${numBolsas !== 1 ? 's' : ''}`;
                    } else if (detalle.kilos_totales && producto.peso_kg) {
                      // CASO PRINCIPAL: Tenemos kilos_totales y peso_kg guardados
                      const plural = detalle.cantidad !== 1 ? 's' : '';
                      presentacionDisplay = `${detalle.cantidad.toLocaleString()} ${unidadComercial}${plural} de ${producto.peso_kg} kg`;
                    } else if (detalle.kilos_totales) {
                      // Solo tenemos kilos_totales (sin peso_kg del producto)
                      presentacionDisplay = `${detalle.kilos_totales.toLocaleString()} kg total`;
                    } else if (producto.peso_kg) {
                      // Tenemos peso_kg pero no kilos_totales guardados - mostrar presentación del producto
                      const plural = detalle.cantidad !== 1 ? 's' : '';
                      presentacionDisplay = `${detalle.cantidad.toLocaleString()} ${unidadComercial}${plural} de ${producto.peso_kg} kg`;
                    } else {
                      // Producto sin conversión a kilos - mostrar guion
                      presentacionDisplay = "-";
                    }
                    
                    return (
                      <TableRow key={detalle.id}>
                        <TableCell className="font-mono text-sm">{producto.codigo}</TableCell>
                        <TableCell>
                          {getDisplayName(producto)}
                        </TableCell>
                        <TableCell className="text-right">
                          {/* Si tiene peso_kg, la cantidad son unidades comerciales */}
                          {producto.peso_kg && producto.precio_por_kilo
                            ? `${detalle.cantidad} ${unidadComercial}${detalle.cantidad !== 1 ? 's' : ''}`
                            : producto.precio_por_kilo
                              ? `${detalle.cantidad} kg`
                              : `${detalle.cantidad} ${unidadComercial}${detalle.cantidad !== 1 ? 's' : ''}`
                          }
                          {/* Mostrar kilos totales entre paréntesis si están disponibles */}
                          {detalle.kilos_totales && (
                            <span className="text-muted-foreground text-xs ml-1">
                              ({detalle.kilos_totales.toLocaleString()} kg)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-semibold text-primary">
                          {presentacionDisplay}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${formatCurrency(detalle.precio_unitario)}
                          {producto.precio_por_kilo && <span className="text-xs text-muted-foreground">/kg</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono">${formatCurrency(detalle.subtotal)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Totales */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-mono">${formatCurrency(pedido.subtotal || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Impuestos:</span>
                  <span className="font-mono">${formatCurrency(pedido.impuestos || 0)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span className="font-mono">${formatCurrency(pedido.total)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No se encontró el pedido</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
